import React, { useEffect, useState } from "react";
import type { TableProps } from "antd";
import {
  Button,
  Flex,
  InputNumber,
  Modal,
  notification,
  Space,
  Table,
  Tooltip,
} from "antd";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import moment from "moment";
import { getCommonState, getExaminerState } from "../../redux/store/getState";
import modifyId from "../../utils/modifyId";
import studentId from "../../utils/studentId";
import { ExamSession } from "../../redux/apiSlice/examinerSlice";
import {
  filterExaminerExamInstances,
  getExaminerExamInstances,
} from "../../redux/apiActions/examinerActions";
import { IconPrimary } from "../../components";
import { DownloadDashedIcon } from "../../assets";
import { clearFilterValues } from "../../redux/apiSlice/commonSlice";
import { EXAM_STATUS } from "../../utils/status";
import axiosClient from "../../api/axiosClient";
import handleApiError from "../../utils/handleError";
import downloadFile from "../../utils/downloadFile";
import useUser from "../../hooks/useUser";

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

const ExaminerSubmissionTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { examinerInstances, loading } = useAppSelector(getExaminerState);
  const { filterFrom, filterTo, atc_id } = useAppSelector(getCommonState);
  const [data, setData] = useState<ExamSession[]>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // examinerEditPermission carries the `admin` bypass every other gate in the app
  // uses (and that the sidebar used to let you onto this page); checking the raw
  // "examiner.edit" string here left admins staring at a permanently dead Approve.
  const { examinerEditPermission: isHaveEditPermission } = useUser();

  useEffect(() => {
    const sortedInstances = [...examinerInstances].sort(
      (a, b) => moment(b.created_at).unix() - moment(a.created_at).unix()
    );

    setData(sortedInstances);
  }, [examinerInstances]);

  useEffect(() => {
    if (filterFrom && filterTo && atc_id) {
      dispatch(
        filterExaminerExamInstances({
          from: filterFrom,
          to: filterTo,
          atc_id,
        })
      );
    } else {
      dispatch(getExaminerExamInstances());
    }
  }, [filterFrom, filterTo, atc_id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearFilterValues());
    };
  }, [dispatch]);

  // Approving is the examiner's one chance to mark the report — the score lands in a
  // "Report" category and is added to both the earned and total score before pass/fail is
  // recomputed. It used to post the exam's maximum report_score unconditionally, so every
  // approval silently awarded 100% on the report. Collect the real mark first.
  const [grading, setGrading] = useState<{
    record: ExamSession | null;
    score: number | null;
    busy: boolean;
  }>({ record: null, score: null, busy: false });

  const closeGrading = () =>
    setGrading({ record: null, score: null, busy: false });

  async function approve() {
    const { record, score } = grading;
    if (!record || score === null) return;
    setGrading((g) => ({ ...g, busy: true }));
    try {
      await axiosClient.post(`examinstances/${record.id}/approve`, {
        report_score: score,
      });
      notification.success({
        message: "Approved successfully",
      });
      closeGrading();
      dispatch(getExaminerExamInstances());
    } catch (error: any) {
      setGrading((g) => ({ ...g, busy: false }));
      handleApiError(error);
    }
  }

  async function getSubmissionAndDownloadFile(id: string, username: string) {
    try {
      const res = await axiosClient.get(`examinstances/${id}/submission`);
      downloadFile(res.data.report_url, `${username}_report`);
    } catch (error: any) {
      handleApiError(error);
    }
  }

  // A greyed-out Approve is ambiguous — say which of the three gates is closed.
  const approveHint = (record: ExamSession) => {
    if (!isHaveEditPermission) return "You need the examiner.edit permission";
    if (record.status === "report_pending")
      return "Waiting for the candidate to upload their report";
    if (record.status === "completed") return "Already approved and closed";
    if (record.status !== "in_review")
      return `Cannot approve an attempt that is ${
        EXAM_STATUS[record.status] || record.status
      }`;
    return "Grade the report and close this attempt";
  };

  const columns: ColumnsType<ExamSession> = [
    {
      title: "Date and Time",
      dataIndex: "created_at",
      render: (created_at) =>
        `${moment(created_at).format("DD/MM/YYYY hh:mmA")}`,
    },
    {
      title: "Student ID",
      dataIndex: "user",
      render: (user) => <>{studentId(user)}</>,
    },

    {
      title: "Username",
      dataIndex: "user",
      render: (user) => <>{user.username}</>,
    },
    {
      title: "Exam ID",
      dataIndex: "exam",
      render: (exam) => <>{modifyId(exam.id)}</>,
    },

    {
      title: "Exam Name",
      dataIndex: "exam",
      render: (exam) => <>{exam.name}</>,
    },

    {
      title: "Submission",
      dataIndex: "exam",
      render: (_, record) => (
        <Flex justify="center">
          <IconPrimary
            onClick={() =>
              getSubmissionAndDownloadFile(record.id, record.user.username)
            }
            disabled={!isHaveEditPermission}
          >
            <DownloadDashedIcon />
          </IconPrimary>
        </Flex>
      ),
    },

    {
      // This used to read exam.report_score || exam.score — the exam's *configured*
      // maximum, identical for every candidate who sat it. Show what the candidate
      // actually earned, out of the total that was live at the time.
      title: "Exam Score",
      render: (_, record) => {
        const res = record.exam_instance_result;
        return (
          <Flex justify="center">
            {res?.id ? `${res.score} / ${res.total_score}` : "—"}
          </Flex>
        );
      },
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (status) => <>{EXAM_STATUS[status] || status}</>,
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title={approveHint(record)}>
            {/* span keeps the tooltip alive over a disabled button */}
            <span>
              <Button
                style={{
                  background: "#565656",
                  borderRadius: 12,
                  boxShadow: "0px 4px 4px 0px #00000040",
                  fontWeight: 500,
                  border: "none",
                }}
                type="primary"
                disabled={!isHaveEditPermission || record.status !== "in_review"}
                onClick={() =>
                  setGrading({ record, score: null, busy: false })
                }
              >
                Approve
              </Button>
            </span>
          </Tooltip>
        </Space>
      ),
    },
  ];
  const maxReportScore = grading.record?.exam?.report_score ?? 0;

  return (
    <>
      <Table
        style={{ marginTop: 20, whiteSpace: "pre" }}
        loading={loading.get}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data}
        onChange={(p) =>
          setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })
        }
        pagination={pagination}
        rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
      />

      <Modal
        title="Approve submission"
        open={!!grading.record}
        onCancel={closeGrading}
        onOk={approve}
        okText="Approve"
        confirmLoading={grading.busy}
        okButtonProps={{
          disabled: grading.score === null || grading.score > maxReportScore,
        }}
        destroyOnClose
      >
        <p style={{ marginBottom: 8 }}>
          {grading.record?.user?.username} — {grading.record?.exam?.name}
        </p>
        <p style={{ marginBottom: 8 }}>
          Report score (max {maxReportScore}). This is added to the candidate's
          MCQ score, then pass/fail is recomputed at 70%.
        </p>
        <InputNumber
          autoFocus
          min={0}
          max={maxReportScore}
          precision={0}
          value={grading.score}
          onChange={(score) => setGrading((g) => ({ ...g, score }))}
          style={{ width: "100%" }}
          placeholder="Enter the mark awarded for the report"
        />
      </Modal>
    </>
  );
};

export default ExaminerSubmissionTable;
